import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { useMemo } from 'react';

import rightArrowIcon from '@/assets/right_arrow.svg';

import { useTheme } from '@/context/theme/UseTheme.jsx';

import { UserStatus } from '@/utils/constants.js';
import { AppointmentStatus, BasicStatus, InsuranceType } from '@/utils/helpers.js';

import { useLocationStore, useTodaysAppointmentsStore } from '@/zustandStore.js';

import AppointmentStatusTag from './AppointmentStatusTag.jsx';
import StatusLight from './StatusLight.jsx';

const AppointmentEntry = ({
    appointment,
    selectedAppointment,
    updateSelectedAppointment,
    detailsLoading,
    professionalName,
    patientName,
}) => {
    const checkinData = useTodaysAppointmentsStore((state) => state.checkinData);
    const selectedLocation = useLocationStore((state) => state.selectedLocation);
    const { colors } = useTheme();

    const config = useMemo(() => {
        if (appointment.user_status === UserStatus.UNKNOWN) return selectedLocation.dcConfig;
        return selectedLocation.configs.find((c) => c.instance_id[0].text === appointment.links.dc_instance_id);
    }, [selectedLocation, appointment]);

    let backgroundColor;
    if (appointment.professional?.key) {
        backgroundColor = selectedAppointment?.id === appointment.id ? 'rgba(76,114,109,0.2)' : 'transparent';
    } else {
        backgroundColor = selectedAppointment?.id === appointment.id ? 'rgba(249,177,21,0.2)' : 'rgba(249,177,21,0.1)';
    }
    if (appointment.inactive) {
        backgroundColor = selectedAppointment?.id === appointment.id ? 'rgba(76,114,109,0.2)' : '#f8f8f8';
    }
    if (appointment.user_status === UserStatus.UNKNOWN) {
        backgroundColor = selectedAppointment?.id === appointment.id ? 'rgba(76,114,109,0.2)' : 'transparent';
    }

    let borderColor;
    if (!appointment.is_external && appointment.user_status !== UserStatus.UNKNOWN) {
        borderColor = colors.primary;
    } else if (appointment.user_status !== UserStatus.UNKNOWN && !appointment.data?.generated_id) {
        borderColor = '#ccc';
    } else {
        borderColor = selectedAppointment?.id === appointment.id ? colors.secondary : '#fff';
    }

    const appointmentInsuranceCardStatus = useMemo(() => {
        if (
            appointment.user_status === UserStatus.UNKNOWN ||
            appointment.data?.insurance_coverage_type?.toUpperCase() !== InsuranceType.PUBLIC
        ) {
            return BasicStatus.NOT_RELEVANT;
        }

        if (appointment.data?.is_insurance_card_active) return BasicStatus.EGK_READ;

        return BasicStatus.EGK_NOT_READ;
    }, [appointment]);

    const appointmentStatusTag = useMemo(() => {
        if (appointment.status === 'CANCELLED') {
            return <AppointmentStatusTag status={AppointmentStatus.CANCELLED} config={config} />;
        }

        if (!checkinData[appointment.id]) {
            return <AppointmentStatusTag status={AppointmentStatus.PENDING} config={config} />;
        }

        return (
            <AppointmentStatusTag
                status={checkinData[appointment.id].appointment?.checkin_status}
                checkinTime={checkinData[appointment.id].appointment?.checkin_time}
                ticketNumber={checkinData[appointment.id].number}
                config={config}
            />
        );
    }, [checkinData, appointment]);

    return (
        <TableBody
            key={appointment.id}
            style={{
                cursor: !detailsLoading ? 'pointer' : 'not-allowed',
                userSelect: 'none',
                opacity: appointment.status !== 'CANCELLED' || selectedAppointment?.id === appointment.id ? 1 : 0.4,
                background: backgroundColor,
                borderLeft: `4px solid ${borderColor}`,
            }}
        >
            <TableRow
                sx={{ '& > *': { borderBottom: 'unset', paddingBottom: 'inherit' } }}
                onClick={() => updateSelectedAppointment(appointment)}
            >
                <TableCell>
                    {(appointment.start &&
                        new Date(appointment.start).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                        })) ||
                        (appointment.appointment?.checkin_time &&
                            new Date(appointment.appointment.checkin_time).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                            })) ||
                        '-'}
                </TableCell>
                <TableCell style={{ position: 'relative' }}>
                    <StatusLight
                        style={{
                            position: 'absolute',
                            left: '0rem',
                            top: '50%',
                            transform: 'translate(-150%, -50%)',
                            width: '0.5rem',
                            height: '0.5rem',
                        }}
                        status={appointmentInsuranceCardStatus}
                    />
                    {patientName}
                </TableCell>
                <TableCell>{professionalName || '-'}</TableCell>
                <TableCell align="right">{appointmentStatusTag}</TableCell>
                <TableCell>
                    <img src={rightArrowIcon} alt="" />
                </TableCell>
            </TableRow>
        </TableBody>
    );
};

export default AppointmentEntry;
